import { createAction } from 'Lib/reduxUtils';

import {
  AUTH_HEALTHY,
  AUTH_LOGOUT,
  AUTH_OUTAGE,
  CHECK_AUTH_HEALTH,
  CLEAR_LOGIN_ERROR,
  GUEST_LOGIN_START,
  GUEST_LOGIN_SUCCESS,
  GUEST_UNAUTHORIZED,
  HANDLE_AUTH_RESPONSE,
  LOGIN_FAILURE,
  LOGIN_START,
  LOGIN_SUCCESS,
  LOGOUT_FAILURE,
  LOGOUT_SUCCESS,
  REAUTH,
  RESEND_EMAIL_OTP,
  RESEND_PHONE_OTP,
  SET_APPEAL_TOKEN,
  SET_AUTH_FUNNEL_SESSION_ID,
  SET_AUTH_TOKEN,
  SET_AUTH_TOKEN_EXPIRATION,
  SET_CAPTCHA_KEY,
  SET_CAPTCHA_TYPE,
  SET_MAGIC_LINK_TYPE,
  SET_ONBOARDING_TOKEN,
  SET_ONBOARDING_USER_ID,
  SET_REFRESH_TOKEN,
  SET_SELFIE_CHALLENGE_TOKEN,
  SET_SELFIE_CHALLENGE_V2_STATUS,
  SET_SMS_CAPTCHA_TOKEN,
  SUBMIT_ACCOUNT_RECOVERY_OTP,
  SUBMIT_EMAIL,
  SUBMIT_EMAIL_OTP,
  SUBMIT_FACEBOOK_TOKEN,
  SUBMIT_GOOGLE_TOKEN,
  SUBMIT_LINE_TOKEN,
  SUBMIT_MAGIC_LINK_OTP,
  SUBMIT_PHONE_NUMBER,
  SUBMIT_PHONE_OTP,
} from 'Store/mfa/constants';

import { SelfieChallengeV2Status } from 'Containers/SelfieChallengeV2/types';

export const loginStart = createAction(
  LOGIN_START,
  (loginType: string, token?: string, clientId?: string) => ({
    loginType,
    token,
    clientId,
  })
);

export const guestLoginStart = createAction(GUEST_LOGIN_START);

export const reauth = createAction(REAUTH);

// Auth State
export const authOutage = createAction(AUTH_OUTAGE);

export const authHealthy = createAction(AUTH_HEALTHY);

export const checkAuthHealth = createAction(CHECK_AUTH_HEALTH);

// Login
export const resendEmailOtp = createAction(RESEND_EMAIL_OTP);

export const submitPhoneNumber = createAction(
  SUBMIT_PHONE_NUMBER,
  (phoneNumber: string, isAccountRecovery: boolean = false) => ({
    phoneNumber,
    isAccountRecovery,
  })
);

export const resendPhoneOTP = createAction(
  RESEND_PHONE_OTP,
  (isAccountRecovery: boolean = false, isMagicLink: boolean = false) => ({
    isAccountRecovery,
    isMagicLink,
  })
);

export const submitPhoneOtp = createAction(
  SUBMIT_PHONE_OTP,
  (
    phoneNumber: string,
    otp: string,
    isAccountRecovery: boolean = false,
    isMagicLink: boolean = false
  ) => ({
    phoneNumber,
    otp,
    isAccountRecovery,
    isMagicLink,
  })
);

export const submitEmail = createAction(SUBMIT_EMAIL, (email: string, marketingOptIn: boolean) => ({
  email,
  marketingOptIn,
}));

export const submitEmailOtp = createAction(SUBMIT_EMAIL_OTP, (email: string, otp: string) => ({
  email,
  otp,
}));

export const submitGoogleToken = createAction(
  SUBMIT_GOOGLE_TOKEN,
  (googleIdToken: string, marketingOptIn: boolean | null, isLoginStart?: boolean) => ({
    googleIdToken,
    marketingOptIn,
    isLoginStart,
  })
);

export const submitFacebookToken = createAction(SUBMIT_FACEBOOK_TOKEN, (facebookToken: string) => ({
  facebookToken,
}));

export const submitAccountRecoveryOtp = createAction(
  SUBMIT_ACCOUNT_RECOVERY_OTP,
  (recoveryOtp?: string) => ({ recoveryOtp })
);

export const submitMagicLinkOtp = createAction(
  SUBMIT_MAGIC_LINK_OTP,
  (otp: string, type?: string) => ({ otp, type })
);

export const loginSuccess = createAction(LOGIN_SUCCESS);
export const guestLoginSuccess = createAction(GUEST_LOGIN_SUCCESS, (token: string) => ({ token }));

export const loginFailure = createAction(LOGIN_FAILURE, (error: any) => ({ error }));

export const clearLoginError = createAction(CLEAR_LOGIN_ERROR);

// Auth Tokens
export const setRefreshToken = createAction(SET_REFRESH_TOKEN, (refreshToken: string) => ({
  refreshToken,
}));

export const setSmsCaptchaToken = createAction(
  SET_SMS_CAPTCHA_TOKEN,
  (smsCaptchaToken: string | null) => ({ smsCaptchaToken })
);

export const setAuthToken = createAction(SET_AUTH_TOKEN, (authToken: string) => ({ authToken }));

export const setSelfieChallengeToken = createAction(
  SET_SELFIE_CHALLENGE_TOKEN,
  (selfieChallengeToken: string) => ({ selfieChallengeToken })
);

export const setSelfieChallengeV2Status = createAction(
  SET_SELFIE_CHALLENGE_V2_STATUS,
  (selfieStatus: SelfieChallengeV2Status) => ({ selfieStatus })
);

export const setAuthTokenExpiration = createAction(
  SET_AUTH_TOKEN_EXPIRATION,
  (authTokenExpiration: number | null) => ({ authTokenExpiration })
);

export const setAppealToken = createAction(SET_APPEAL_TOKEN, (appealToken: string) => ({
  appealToken,
}));

// Onboarding
export const setOnboardingToken = createAction(SET_ONBOARDING_TOKEN, (onboardingToken: string) => ({
  onboardingToken,
}));

export const setOnboardingUserId = createAction(
  SET_ONBOARDING_USER_ID,
  (onboardingUserId: string) => ({ onboardingUserId })
);

export const setCaptchaType = createAction(SET_CAPTCHA_TYPE, (captchaType: string) => ({
  captchaType,
}));

export const setCaptchaKey = createAction(SET_CAPTCHA_KEY, (captchaKey: string) => ({
  captchaKey,
}));

// Logout
export const authLogout = createAction(AUTH_LOGOUT);

export const logoutSuccess = createAction(LOGOUT_SUCCESS);

export const logoutFailure = createAction(LOGOUT_FAILURE);

// Internal
export const setAuthFunnelSessionId = createAction(SET_AUTH_FUNNEL_SESSION_ID);

// Line Auth
export const submitLineToken = createAction(
  SUBMIT_LINE_TOKEN,
  (lineToken: string | null, clientId: string) => ({
    lineToken,
    clientId,
  })
);

export const handleAuthResponse = createAction(
  HANDLE_AUTH_RESPONSE,
  (response: any, isAccountRecovery?: boolean, isMagicLink?: boolean) => ({
    response,
    isAccountRecovery,
    isMagicLink,
  })
);

export const guestUnauthorized = createAction(GUEST_UNAUTHORIZED);

export const setMagicLinkType = createAction(SET_MAGIC_LINK_TYPE, (type: string) => type);
